
import { useContext, useEffect, useMemo, useRef, useState } from 'react';

//context
import { OrgSettingsUsersContext } from '@/context/OrgSettingsUsersContext';

//interfaces
import { NotificationStatus } from  '@/interfaces/languages';
import { IRequestDataForIP } from  '@/interfaces/passkeys';

//api
import { getApproximateLocation, listRecoveryRequestsForIP, listRecoveryRequestsForUser } from '@/api/orgSettings/passkeys';

//hooks
import UseRecoveryRequestsBreadcrumbs from './RecoveryRequestsBreadcrumbs.hooks';
import { useTimer } from  '@/components/hooks/utility/useTimer';

//helpers
import { formatLocation } from  '@/helpers/Location';
import { formatDateToDDMMYYYYHHMM } from  '@/helpers/Dates';

//components
import { BasicButton, ISimpleTableColumn, ISimpleTableProps, ISimpleTableRows, SimpleSortBy } from 'thrive-components';
import RequestMessage from  '@/components/elements/passkeys/RequestMessage/requestMessage';



const UseRecoveryRequestsModal = ({showModal}:{showModal: boolean}) => {
    const {userId} = useContext(OrgSettingsUsersContext);
    const { breadcrumbs, setBreadcrumb } = UseRecoveryRequestsBreadcrumbs({onBreadcrumbClick});
    const [isSoftResetting, setIsSoftResetting]=useState<boolean>(false);
    const [tableHeight, setTableHeight] = useState<number>(88);
    const [notificationDetails, setNotificationDetails]=useState<{show: boolean, status: NotificationStatus | null, message: string}>();
    const [sortBy, setSortBy]=useState<{sortBy: SimpleSortBy, sortType: string }>({sortBy: "none" as SimpleSortBy, sortType: ""})
    const [requestData, setRequestData]=useState<any[]>([]);
    const [requestDataForIP, setRequestDataForIP]=useState<IRequestDataForIP[]>([]);
    const [isLoading, setIsLoading]=useState<boolean>(false);
    const [isLoadingMore, setIsLoadingMore]=useState<boolean>(false);
    const [isGettingLocation, setIsGettingLocation]=useState<boolean>(false);
    const [moreRequestsAvailable, setMoreRequestsAvailable]=useState<boolean>(false);
    const [abortController, setAbortController] = useState<AbortController | null>(null);
    const [skip, setSkip]=useState<number>(0);
    const [searchTerm, setSearchTerm]=useState<string>("");
    const [location, setLocation]=useState<string>("");
    const [ipAddress, setIpAddress]=useState<string>("");
    const [displayMessage, setDisplayMessage]=useState<IRequestDataForIP | undefined>(undefined);
    const autoScrollHandler =  !isLoading && !isLoadingMore && moreRequestsAvailable;
    const isInitialLoad = useRef(true);
    const simpleBarRef = useRef();
    const prevSearchTermRef = useRef<string>("");
    const emptyTableMessage = "No results found";
    const columns: Array<ISimpleTableColumn> = [
        { 
            title: "Last Received Date", 
            isToggleButton: true,                    
            onSortAscendingClick: () => setSortBy({sortBy: "desc" as SimpleSortBy, sortType:"latestReceivedDate"}), 
            onSortDescendingClick: () => setSortBy({sortBy:"asc" as SimpleSortBy, sortType:"latestReceivedDate"}), 
        },
        { 
            title: "IP Address", 
            isToggleButton: false,                    
        },
        { 
            title: "Number of requests", 
            isToggleButton: true,                    
            onSortAscendingClick: () => setSortBy({sortBy: "desc" as SimpleSortBy, sortType:"numberOfRequests"}), 
            onSortDescendingClick: () => setSortBy({sortBy: "asc" as SimpleSortBy, sortType:"numberOfRequests"}), 
        },
        { 
            title: "Latest Message", 
            isToggleButton: false,                    
        },
    ];

    const requestsForIpColumns: Array<ISimpleTableColumn> = [
        { 
            title: "Date", 
            isToggleButton: true,                    
            onSortAscendingClick: () => setSortBy({sortBy: "desc" as SimpleSortBy, sortType:"receivedDate"}), 
            onSortDescendingClick: () => setSortBy({sortBy:"asc" as SimpleSortBy, sortType:"receivedDate"}), 
        },
        { 
            title: "Message", 
            isToggleButton: false,                    
        },
    ];
    
    const tableContent = useMemo<ISimpleTableProps>(() => (
        {
            simpleTableContent: {columns: columns, rows: formatTableRows()},
            isInfiniteScroll: true,
            isLoading: isLoading,
            noDataMessage: emptyTableMessage,
            infiniteScrollFetchCondition: moreRequestsAvailable,
            hideActionsColumn: true
        }), [requestData, isLoading]
    );

    const requestsForIpTableContent = useMemo<ISimpleTableProps>(() => (
        {
            simpleTableContent: {columns: requestsForIpColumns, rows: formatTableRows()},
            isInfiniteScroll: true,
            isLoading: isLoading,
            noDataMessage: emptyTableMessage,
            infiniteScrollFetchCondition: moreRequestsAvailable,
            hideActionsColumn: true
        }), [requestDataForIP, isLoading]
    );

    useEffect(() => {
        if (!showModal || isInitialLoad.current) return;
        const prevSearchTerm = prevSearchTermRef.current;
        const searchCleared = prevSearchTerm.length > 0 && searchTerm.length === 0;

        if (sortBy.sortType || searchTerm || searchCleared) {
            getData();
        }
        prevSearchTermRef.current = searchTerm;
    }, [sortBy, searchTerm]);

    useEffect(()=>{ 
        if (!showModal) return; 
        softReset(); 
    }, [ipAddress])

    useEffect(() => {
        if (!isSoftResetting) return;
        getData();
    }, [isSoftResetting]);

    useEffect(() => {
        if (!showModal) {
            cleanUp();
        } else {
            isInitialLoad.current = false;
            getData();
        }
    },[showModal])

    useEffect(() => {
        if ((requestDataForIP?.length > 0) || (requestData?.length > 0)) {
            setTableHeight(getTableHeight())
        } else {
            setTableHeight(88);
        }
    },[requestData, requestDataForIP]);

    useTimer(notificationDetails?.show ?? false, ()=>setNotificationDetails({show: false, status:null, message: ""}), 3000);
    
    function formatTableRows(){
        if (ipAddress) {
            return requestDataForIP.map((request: IRequestDataForIP) => ({
                content: [
                    formatDateToDDMMYYYYHHMM(request.createdAt),
                    <RequestMessage 
                        message={request.message}
                        onClick={()=>setDisplayMessage(request)}
                    />,
                ],
                controls : []
            }))
        }
        return requestData.map((request: any) => ({
            content: [
                formatDateToDDMMYYYYHHMM(request.latestReceivedDate),
                <span className='ip-address-wrapper'>{request.ipAddress}</span>,
                request.numberOfRequests,
                <span className='latest-message-wrapper'>
                    <span className='latest-message'>  
                        {request.latestMessage ? request.latestMessage : "-"}
                    </span>
                    <BasicButton
                        onButtonClick={()=>{setIpAddress(request.ipAddress), setBreadcrumb(request.id, request.ipAddress)}}
                        ariaLabel={`View details of Recovery Request from the IP Address : ${request.ipAddress}`}
                        className='view-address-details-button'
                        content={"View"}
                    />
                </span>
            ],
            controls : []
        })) as ISimpleTableRows[]
    }

    function onGetLocationClick() {
        setIsGettingLocation(true);

        const showError = () =>
        setNotificationDetails({
            show: true,
            status: NotificationStatus.error,
            message: "Unable to get location.",
        });

        getApproximateLocation(ipAddress)
            .then((data) => {
                const locationString = formatLocation(data);
                if (locationString) {
                    setLocation(locationString)
                } else {
                    showError();
                }
            })
            .catch(() => {
                showError();    
            })
            .finally(() =>{
                setIsGettingLocation(false)
            });
    }

    function getData(){
        if (ipAddress) {
            getRequestDataForIP();
        } else {
            getRequestData();
        }
    }

    function getRequestDataForIP(isPagination?: boolean) {
        if (abortController) { abortController.abort(); }
        const newAbortController = new AbortController();
        setAbortController(newAbortController);
        
        setLoaders(isPagination);
        
        const count = requestDataForIP?.length ?? 0;
        const skipCount = isPagination ? skip : 0;
        const params = {
            ...(searchTerm && { searchTerm }),
            ...(sortBy?.sortType && {
                sortBy: sortBy.sortType,
                sort: sortBy.sortBy,
            }),
            top: 20,
            skip: skipCount,
        };
        
        listRecoveryRequestsForIP(Number(userId), ipAddress, params, { signal: newAbortController.signal })
        .then((data) => {
            if (isPagination) {
                setRequestDataForIP((prevRequestData: any) => [...(prevRequestData || []), ...data.items]);
            } else {
                setRequestDataForIP(data.items);
            }
            setMoreRequestsAvailable(data.totalItemCount > (data.items.length + count));
            setSkip(data.nextSkip);
        })
        .catch((error) => {
            if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
                setNotificationDetails({
                    show: true,
                    status: NotificationStatus.error,
                    message: `Could not source request data for IP: ${ipAddress}`,
                });
            }
        })
        .finally(() =>{
            stopLoading()
        });
    }

    function getRequestData(pagination?: boolean) {
        const count = requestData?.length ?? 0;
        const skipCount = pagination ? skip : 0;
        setLoaders(pagination);

        if (abortController) { abortController.abort(); }
        const newAbortController = new AbortController();
        setAbortController(newAbortController);

        const params = {
            ...(searchTerm && { searchTerm }),
            ...(sortBy?.sortType && {
                sortBy: sortBy.sortType,
                sort: sortBy.sortBy,
            }),
            top: 20,
            skip: skipCount,
        };
        listRecoveryRequestsForUser(Number(userId), params, { signal: newAbortController.signal })
        .then((data) => {
            if (pagination) {
                setRequestData((prevRequestData: any) => [...(prevRequestData || []), ...data.items]);
            } else {
                setRequestData(data.items);
            }
            setMoreRequestsAvailable(data.totalItemCount > (data.items.length + count));
            setSkip(data.nextSkip);
        })
        .catch((error) => {
            if (error.name !== "AbortError" && error.code !== "ERR_CANCELED") {
                setNotificationDetails({
                    show: true,
                    status: NotificationStatus.error,
                    message: "Could not source request data",
                });
            }
        })
        .finally(() =>{
            stopLoading()
        });
    }

    function getTableHeight(): number {
        const rows = formatTableRows();
        if(!rows.length) {
            return  88
        }
        return rows.length >= 9 ? 400 : rows.length * 40 + 35
    }

    function setLoaders(pagination?: boolean) {
        if (pagination) { 
            setIsLoadingMore(true) 
        } else { 
            setIsLoading(true)
        }
    }

    function onBreadcrumbClick() {
        setIpAddress("");
    }

    function getMoreData() {
        if (isLoadingMore) return;
        if (ipAddress) {
            getRequestDataForIP(true);
        } else {
            getRequestData(true);
        }
    }

    function resetSort() {
        setSortBy({sortBy: "none" as SimpleSortBy, sortType: ""});
    }

    function basicReset() {
        setIsLoadingMore(false);
        setIsLoading(false);
        resetSort();
        setSearchTerm("");
        setSkip(0);
        setRequestData([]);
        setLocation("");
        setIsGettingLocation(false);
        setDisplayMessage(undefined);
    }

    function softReset() {
        basicReset();
        setRequestDataForIP([]);
        setIsSoftResetting(true);
    }

    function cleanUp() {
        basicReset();
        setIpAddress("");
    }

    function stopLoading() {
        setIsLoading(false)
        setIsLoadingMore(false);
        setIsSoftResetting(false);
    }
    
    return {
        breadcrumbs,
        simpleBarRef,
        autoScrollHandler,
        tableHeight,
        requestDataForIP,
        searchTerm,
        isGettingLocation,
        location,
        notificationDetails,
        requestsForIpTableContent,
        tableContent,
        displayMessage,
        ipAddress,
        setDisplayMessage,
        setSearchTerm,
        getMoreData,
        onGetLocationClick,
    }
}

export default UseRecoveryRequestsModal;